import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IconReceipt } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import SaveError from "src/components/shared/SaveError";
import { FormField, selectA11y } from "src/pages/catalogue/FormBits";
import FileViewer from "src/pages/request/FileViewer";
import { casesApi, openReceipt } from "./api";
import { failureWords, timeWords } from "./decisions";
import { KIND_LABELS, stepDetails, toLocalInput, wallWords } from "./filingWords";
import { toneColor } from "./workspace";

/**
 * Filing the application with the embassy or visa centre (API
 * docs/filing.md, migration 018): its visa's steps after submission, copied
 * to the case and ticked off with what each produced (application numbers,
 * the appointment, a fee paid outside Safrati). The customer sees them on
 * their application page. Nothing shows before the database update.
 */
export default function Filing({ caseData, canEdit, onChanged }) {
  const dispatch = useDispatch();
  const filing = caseData.filing;
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [starting, setStarting] = useState(false);
  if (!filing?.migrated) return null;
  // A cancelled, refused or expired case has nothing to file (nadia-03).
  const closed = ["cancelled", "rejected", "expired"].includes(caseData.status);
  const travellers = caseData.travellers.map((t) => ({ request_id: t.request_id, name: t.name, number: t.number }));
  const run = async (call, done) => {
    try {
      const response = await call();
      if (response?.data) onChanged(response.data);
      if (done) toast.success(done);
      return true;
    } catch {
      return false;
    }
  };
  const tick = (step, value) => run(() => casesApi.filingStep(caseData.id, step.id, { done: value }, dispatch));

  return (
    <Card variant="outlined" id="case-filing" tabIndex={-1} sx={{ outline: "none", scrollMarginTop: 180 }}>
      <CardContent>
        <Typography variant="h6" component="h2" mb={0.5}>
          Filing with the embassy
        </Typography>
        {!filing.started && caseData.status === "incomplete" ? (
          <Typography variant="body2" color="text.secondary">
            Filing starts once the customer submits the application.
          </Typography>
        ) : !filing.started && closed ? (
          <Typography variant="body2" color="text.secondary">
            This case is closed ({caseData.status === "rejected" ? "refused" : caseData.status}): there is nothing to file.
          </Typography>
        ) : !filing.started ? (
          <NotStarted filing={filing} canEdit={canEdit} onStart={() => setStarting(true)} onAdd={() => setAdding(true)} />
        ) : (
          <Stack spacing={1.25} mt={1}>
            <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              {filing.steps.map((step) => {
                const details = stepDetails(step, travellers);
                const label = step.kind === "fee" ? `Paid: ${step.title}` : `Done: ${step.title}`;
                return (
                  <Box
                    component="li"
                    key={step.id}
                    id={step.kind === "fee" ? `case-fee-${step.id}` : undefined}
                    tabIndex={step.kind === "fee" ? -1 : undefined}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, borderRadius: 1, outline: "none", scrollMarginTop: 180 }}
                  >
                    <Checkbox
                      checked={step.done}
                      disabled={!canEdit}
                      onChange={(event) => tick(step, event.target.checked)}
                      inputProps={{ "aria-label": label }}
                      sx={{ mt: -0.75, ml: -1 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={600} sx={{ textDecoration: step.done ? "line-through" : "none", color: step.done ? "text.secondary" : "text.primary" }}>
                        {step.title}
                      </Typography>
                      {details.map((line) => (
                        <Typography key={line} variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                          {line}
                        </Typography>
                      ))}
                      {step.kind === "fee" && step.customer_paid_at ? (
                        <CustomerPaid step={step} onView={() => setReceipt(step)} />
                      ) : null}
                      {step.done && step.done_at ? (
                        <Typography variant="caption" color="text.secondary">
                          {step.kind === "fee" ? "Paid" : step.kind === "appointment" ? "Booked" : "Done"} {wallWords(step.done_at) || step.done_at}
                          {step.done_by ? ` by ${step.done_by}` : ""}
                        </Typography>
                      ) : null}
                    </Box>
                    {canEdit ? (
                      <ActionButton variant="text" size="small" onClick={() => setEditing(step)} aria-label={`Edit: ${step.title}`}>
                        Edit
                      </ActionButton>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
            {canEdit ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ActionButton variant="outlined" size="small" onClick={() => setAdding(true)}>
                  Add a step
                </ActionButton>
                <ActionButton variant="outlined" size="small" onClick={() => setEmailing(true)}>
                  Email the customer
                </ActionButton>
              </Stack>
            ) : null}
          </Stack>
        )}
      </CardContent>
      {editing ? (
        <StepDialog
          step={editing}
          travellers={travellers}
          onClose={() => setEditing(null)}
          onSave={async (values) => {
            const ok = await run(() => casesApi.filingStep(caseData.id, editing.id, values, dispatch), "Saved");
            if (ok) setEditing(null);
          }}
          onRemove={async () => {
            const ok = await run(() => casesApi.filingRemove(caseData.id, editing.id, dispatch), "Step removed");
            if (ok) setEditing(null);
          }}
        />
      ) : null}
      {adding ? (
        <AddStepDialog
          onClose={() => setAdding(false)}
          onAdd={async (kind, title) => {
            const ok = await run(() => casesApi.filingAdd(caseData.id, kind, title, dispatch), "Step added");
            if (ok) setAdding(false);
          }}
        />
      ) : null}
      {starting ? (
        <StartFilingDialog
          filing={filing}
          partner={caseData.partner?.name || null}
          onClose={() => setStarting(false)}
          onStart={async () => {
            const response = await casesApi.filingStart(caseData.id, dispatch, true);
            if (response?.data) onChanged(response.data);
            const steps = response?.data?.steps?.length || filing.plan?.steps?.length || 0;
            toast.success(`Filing started on case #${caseData.id}: ${steps === 1 ? "1 step" : `${steps} steps`}, on the customer’s application page.`);
            setStarting(false);
          }}
        />
      ) : null}
      {receipt ? (
        <ReceiptViewer
          caseId={caseData.id}
          step={receipt}
          canEdit={canEdit}
          onClose={() => setReceipt(null)}
          onChanged={onChanged}
        />
      ) : null}
      {emailing ? (
        <EmailDialog
          customer={filing.customer}
          email={caseData.customer?.email}
          onClose={() => setEmailing(false)}
          onSend={async () => {
            const ok = await run(() => casesApi.filingEmail(caseData.id, dispatch), "Email sent to the customer");
            if (ok) setEmailing(false);
          }}
        />
      ) : null}
    </Card>
  );
}

Filing.propTypes = { caseData: PropTypes.object.isRequired, canEdit: PropTypes.bool, onChanged: PropTypes.func.isRequired };

// A fee the customer paid outside Safrati (API docs/visa-decisions.md,
// migration 023): when they said so and their receipt. Staff still confirm
// with the tick.
function CustomerPaid({ step, onView }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, mt: 0.25 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: step.done ? "text.secondary" : toneColor("warn") }}>
        Customer says paid · {timeWords(step.customer_paid_at)}
      </Typography>
      {step.receipt ? (
        <ActionButton variant="text" size="small" startIcon={<IconReceipt size={16} />} onClick={onView} sx={{ ml: -1 }}>
          View receipt
        </ActionButton>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No receipt added
        </Typography>
      )}
    </Box>
  );
}

CustomerPaid.propTypes = { step: PropTypes.object.isRequired, onView: PropTypes.func.isRequired };

// The customer's receipt in the file viewer, with the tick beside Close
// when the fee isn't ticked yet.
export function ReceiptViewer({ caseId, step, canEdit, onClose, onChanged }) {
  const dispatch = useDispatch();
  const open = useCallback(() => openReceipt(step.id), [step.id]);
  const confirm = async () => {
    try {
      const response = await casesApi.filingStep(caseId, step.id, { done: true }, dispatch);
      if (response?.data) onChanged(response.data);
      toast.success("Fee ticked as paid");
      onClose();
    } catch {
      /* the toast says why */
    }
  };
  return (
    <FileViewer
      file={`receipt-${step.id}`}
      title={`Receipt: ${step.title}`}
      subtitle={step.customer_paid_at ? `Customer says paid · ${timeWords(step.customer_paid_at)}` : undefined}
      open={open}
      onClose={onClose}
      action={
        canEdit && !step.done ? (
          <ActionButton variant="contained" busyLabel="Saving…" onClick={confirm}>
            Mark as paid
          </ActionButton>
        ) : null
      }
    />
  );
}

ReceiptViewer.propTypes = {
  caseId: PropTypes.number.isRequired,
  step: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
};

function NotStarted({ filing, canEdit, onStart, onAdd }) {
  const steps = filing.plan?.steps || [];
  if (!steps.length) {
    return (
      <Stack spacing={1} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          This visa has no steps after submission yet. Set them on the country page (How each visa works here), or add one here.
        </Typography>
        {canEdit ? (
          <ActionButton variant="outlined" size="small" onClick={onAdd}>
            Add a step
          </ActionButton>
        ) : null}
      </Stack>
    );
  }
  return (
    <Stack spacing={1} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary">
        {filing.plan.source === "suggested"
          ? "Suggested from how this visa works (set the visa’s own steps on the country page):"
          : filing.plan.source === "product"
          ? `This product’s own ${steps.length} steps:`
          : `This visa’s ${steps.length} steps:`}
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
        {steps.map((step, index) => (
          <Typography component="li" variant="body2" key={`${index}-${step.title}`}>
            {step.title}
          </Typography>
        ))}
      </Box>
      {canEdit ? (
        <ActionButton variant="contained" size="small" onClick={onStart}>
          Start filing
        </ActionButton>
      ) : null}
    </Stack>
  );
}

NotStarted.propTypes = { filing: PropTypes.object.isRequired, canEdit: PropTypes.bool, onStart: PropTypes.func, onAdd: PropTypes.func };

// Start filing asks first (CMS review round 2, nadia-03): it was the one
// button on the case that acted at once. It says what the customer then
// sees and how to change it afterwards; a failure shows by the button.
export function StartFilingDialog({ filing, partner, onClose, onStart }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const steps = filing.plan?.steps || [];
  const n = steps.length;
  const go = async () => {
    setBusy(true);
    setError("");
    try {
      await onStart();
    } catch (err) {
      setError(failureWords(err, "Filing didn’t start. Nothing changed. Try again."));
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="start-filing-title" aria-describedby="start-filing-text">
      <DialogTitle id="start-filing-title" sx={{ fontSize: "1.125rem", fontWeight: 700, pb: 1 }}>
        Start filing?
      </DialogTitle>
      <DialogContent>
        <Typography id="start-filing-text" color="text.secondary">
          {partner
            ? `${partner} sees ${n === 1 ? "this step" : `these ${n} steps`} for their traveller.`
            : `The customer sees ${n === 1 ? "this step" : `these ${n} steps`} on their application page.`}{" "}
          Afterwards you can tick, change, add or remove steps here. Nobody is emailed now.
        </Typography>
        <Box component="ol" sx={{ m: 0, mt: 1, pl: 2.5 }}>
          {steps.map((step, index) => (
            <Typography component="li" variant="body2" key={`${index}-${step.title}`}>
              {step.title}
            </Typography>
          ))}
        </Box>
      </DialogContent>
      <SaveError message={error} sx={{ mx: 3, mb: 1 }} />
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <ActionButton variant="text" size="medium" onClick={onClose} disabled={busy}>
          Not now
        </ActionButton>
        <ActionButton variant="contained" size="medium" busy={busy} busyLabel="Starting…" onClick={go}>
          Start filing
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

StartFilingDialog.propTypes = { filing: PropTypes.object.isRequired, partner: PropTypes.string, onClose: PropTypes.func.isRequired, onStart: PropTypes.func.isRequired };

// One step's details, by kind: application numbers (form), when, where and
// the booking reference (appointment), the fee's amount, how and by when.
function StepDialog({ step, travellers, onClose, onSave, onRemove }) {
  const [form, setForm] = useState({
    title: step.title,
    note: step.note || "",
    reference: step.reference || "",
    numbers: Object.fromEntries(travellers.map((t) => [String(t.request_id), step.travellers?.[String(t.request_id)] || ""])),
    scheduled_at: toLocalInput(step.scheduled_at),
    place: step.place || "",
    amount: step.amount ?? "",
    currency: step.currency || "",
    pay_how: step.pay_how || "",
    paid_by: step.paid_by || "",
    due_hours: step.due_hours ?? "",
    due_at: toLocalInput(step.due_at),
  });
  const [tried, setTried] = useState(false);
  const [removing, setRemoving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const id = `step-${step.id}`;
  const errors = {};
  if (!form.title.trim()) errors.title = "Give the step a title.";
  if (step.kind === "fee") {
    if (form.amount !== "" && !(Number(form.amount) >= 0)) errors.amount = "Enter the amount as a number.";
    if (form.currency && !/^[A-Za-z]{3}$/.test(form.currency.trim())) errors.currency = "Three letters: USD, EUR, LBP.";
    if (form.due_hours !== "" && !(Number.isInteger(Number(form.due_hours)) && Number(form.due_hours) >= 1 && Number(form.due_hours) <= 720)) errors.due_hours = "Whole hours, from 1 to 720.";
  }
  const shown = tried ? errors : {};
  const save = () => {
    if (Object.keys(errors).length) return setTried(true);
    const values = { title: form.title.trim(), note: form.note.trim() || null };
    if (step.kind === "form") values.travellers = form.numbers;
    if (step.kind === "appointment") Object.assign(values, { scheduled_at: form.scheduled_at || null, place: form.place.trim() || null, reference: form.reference.trim() || null });
    if (step.kind === "fee") {
      Object.assign(values, {
        amount: form.amount === "" ? null : Number(form.amount),
        currency: form.currency.trim().toUpperCase() || null,
        pay_how: form.pay_how.trim() || null,
        paid_by: form.paid_by || null,
        due_hours: form.due_hours === "" ? null : Number(form.due_hours),
        due_at: form.due_at || null,
        reference: form.reference.trim() || null,
      });
    }
    return onSave(values);
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={`${id}-title`}>
      <DialogTitle id={`${id}-title`}>{KIND_LABELS[step.kind] || "Step"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <FormField id={`${id}-name`} label="Title" hint="The customer sees it as one of the steps." error={shown.title}>
            <TextField id={`${id}-name`} fullWidth value={form.title} onChange={(e) => set({ title: e.target.value })} error={Boolean(shown.title)} inputProps={{ maxLength: 160 }} />
          </FormField>
          {step.kind === "form"
            ? travellers.map((t) => (
                <FormField key={t.request_id} id={`${id}-n${t.request_id}`} label={`Application number: ${t.name || `Traveller ${t.number}`}`} optional>
                  <TextField
                    id={`${id}-n${t.request_id}`}
                    fullWidth
                    value={form.numbers[String(t.request_id)]}
                    onChange={(e) => set({ numbers: { ...form.numbers, [String(t.request_id)]: e.target.value } })}
                    inputProps={{ maxLength: 60 }}
                  />
                </FormField>
              ))
            : null}
          {step.kind === "appointment" ? (
            <>
              <FormField id={`${id}-when`} label="When (Beirut time)" optional>
                <TextField id={`${id}-when`} type="datetime-local" value={form.scheduled_at} onChange={(e) => set({ scheduled_at: e.target.value })} sx={{ maxWidth: 260 }} />
              </FormField>
              <FormField id={`${id}-place`} label="Where" optional>
                <TextField id={`${id}-place`} fullWidth value={form.place} onChange={(e) => set({ place: e.target.value })} inputProps={{ maxLength: 255 }} />
              </FormField>
              <FormField id={`${id}-ref`} label="Booking reference" optional>
                <TextField id={`${id}-ref`} fullWidth value={form.reference} onChange={(e) => set({ reference: e.target.value })} inputProps={{ maxLength: 100 }} />
              </FormField>
            </>
          ) : null}
          {step.kind === "fee" ? (
            <>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormField id={`${id}-amount`} label="Amount per traveller" optional error={shown.amount}>
                  <TextField id={`${id}-amount`} type="number" value={form.amount} onChange={(e) => set({ amount: e.target.value })} error={Boolean(shown.amount)} inputProps={{ min: 0, step: "any", inputMode: "decimal" }} sx={{ maxWidth: 180 }} />
                </FormField>
                <FormField id={`${id}-currency`} label="Currency" optional error={shown.currency}>
                  <TextField id={`${id}-currency`} value={form.currency} onChange={(e) => set({ currency: e.target.value })} error={Boolean(shown.currency)} inputProps={{ maxLength: 3 }} sx={{ maxWidth: 120 }} />
                </FormField>
              </Stack>
              <FormField id={`${id}-how`} label="How to pay" optional hint="For example: At OMT, with the TLS reference.">
                <TextField id={`${id}-how`} fullWidth value={form.pay_how} onChange={(e) => set({ pay_how: e.target.value })} inputProps={{ maxLength: 255 }} />
              </FormField>
              <FormField id={`${id}-who`} label="Who pays">
                <TextField id={`${id}-who`} select fullWidth value={form.paid_by} onChange={(e) => set({ paid_by: e.target.value })} SelectProps={selectA11y(`${id}-who`, {})}>
                  <MenuItem value="">Not set yet</MenuItem>
                  <MenuItem value="customer">The customer pays it</MenuItem>
                  <MenuItem value="safrati">Safrati pays it</MenuItem>
                </TextField>
              </FormField>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormField id={`${id}-hours`} label="Due within (hours after booking)" optional error={shown.due_hours}>
                  <TextField id={`${id}-hours`} type="number" value={form.due_hours} onChange={(e) => set({ due_hours: e.target.value })} error={Boolean(shown.due_hours)} inputProps={{ min: 1, max: 720, inputMode: "numeric" }} sx={{ maxWidth: 180 }} />
                </FormField>
                <FormField id={`${id}-due`} label="Deadline (Beirut time)" optional hint="Set when the appointment is booked.">
                  <TextField id={`${id}-due`} type="datetime-local" value={form.due_at} onChange={(e) => set({ due_at: e.target.value })} sx={{ maxWidth: 260 }} />
                </FormField>
              </Stack>
              <FormField id={`${id}-receipt`} label="Receipt number" optional>
                <TextField id={`${id}-receipt`} fullWidth value={form.reference} onChange={(e) => set({ reference: e.target.value })} inputProps={{ maxLength: 100 }} />
              </FormField>
            </>
          ) : null}
          <FormField id={`${id}-note`} label="Note for the customer" optional hint="Shown under the step on their application page.">
            <TextField id={`${id}-note`} fullWidth multiline minRows={2} value={form.note} onChange={(e) => set({ note: e.target.value })} inputProps={{ maxLength: 2000 }} />
          </FormField>
          {removing ? (
            <Alert
              severity="warning"
              action={
                <ActionButton color="inherit" size="small" variant="text" onClick={onRemove}>
                  Remove
                </ActionButton>
              }
            >
              Remove this step from the application?
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <ActionButton variant="text" tone="destructive" size="medium" onClick={() => setRemoving(true)} disabled={removing}>
          Remove this step
        </ActionButton>
        <Stack direction="row" spacing={1}>
          <ActionButton variant="text" size="medium" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" size="medium" onClick={save}>
            Save
          </ActionButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

StepDialog.propTypes = { step: PropTypes.object.isRequired, travellers: PropTypes.array.isRequired, onClose: PropTypes.func, onSave: PropTypes.func, onRemove: PropTypes.func };

function AddStepDialog({ onClose, onAdd }) {
  const [kind, setKind] = useState("other");
  const [title, setTitle] = useState("");
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="add-step-title">
      <DialogTitle id="add-step-title">Add a step</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <FormField id="add-step-kind" label="What kind of step">
            <TextField id="add-step-kind" select fullWidth value={kind} onChange={(e) => setKind(e.target.value)} SelectProps={selectA11y("add-step-kind", {})}>
              {Object.entries(KIND_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </FormField>
          <FormField id="add-step-name" label="Title" hint="As the customer will read it.">
            <TextField id="add-step-name" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 160 }} />
          </FormField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <ActionButton variant="text" size="medium" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton variant="contained" size="medium" onClick={() => onAdd(kind, title.trim())}>
          Add
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

AddStepDialog.propTypes = { onClose: PropTypes.func, onAdd: PropTypes.func };

// What the email says, before it goes.
function EmailDialog({ customer, email, onClose, onSend }) {
  const appointment = customer?.appointment;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="filing-email-title">
      <DialogTitle id="filing-email-title">Email the customer where things stand?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" mb={1.5}>
          To {email || "the customer"}: the appointment, what to bring, fees still to pay and the application numbers, with a link to their application.
        </Typography>
        {appointment ? (
          <Typography variant="body2" mb={1}>
            <b>Appointment:</b> {wallWords(appointment.at)} (Beirut time){appointment.place ? `, ${appointment.place}` : ""}
          </Typography>
        ) : (
          <Alert severity="info" sx={{ mb: 1 }}>
            No appointment time yet: the email says filing has started.
          </Alert>
        )}
        {customer?.bring?.length ? (
          <>
            <Typography variant="body2" fontWeight={600}>
              What to bring
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {customer.bring.map((item) => (
                <Typography component="li" variant="body2" key={item}>
                  {item}
                </Typography>
              ))}
            </Box>
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <ActionButton variant="text" size="medium" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton variant="contained" size="medium" onClick={onSend}>
          Send the email
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

EmailDialog.propTypes = { customer: PropTypes.object, email: PropTypes.string, onClose: PropTypes.func, onSend: PropTypes.func };
